import { ColumnSort } from "@tanstack/react-table";
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
} from "react";

import Skeleton from "veneer/Skeleton";

import DataTable, {
  DataTableProps,
  PageSize,
  PaginationOptions,
  ServerSideOptions,
  TableSelection,
  createColumnHelper,
  getColumnId,
} from "./index";

type SupportedDataTableProps<T, F> = Omit<
  DataTableProps<T, F>,
  | "serverSidePagination"
  | "filter"
  | keyof TableSelection<T>
  | keyof PaginationOptions
  | keyof ServerSideOptions
>;

export type QuerySetSelection<T> = {
  enableRowSelection?: (row: T, isSelected: boolean) => boolean;
  getIdentifier: (item: T) => string;
  selectedItems: ReadonlyArray<T>;
  setSelectedItems: (items: ReadonlyArray<T>) => void;
  // NOTE:  This return type is way too permissive. However, making it `T` is
  // overly restrictive. All we care about is that `getIdentifier(placeholder)`
  // returns a string
  createPlaceholder: (index: number) => object;
};

type QuerySetSelectionProps<T> =
  | QuerySetSelection<T>
  | {
      enableRowSelection?: undefined;
      getIdentifier?: undefined;
      createPlaceholder?: undefined;
      selectedItems?: undefined;
      setSelectedItems?: undefined;
    };

type QuerySetSortingProps =
  | {
      sorting: ColumnSort | null;
      onSortingChange: Dispatch<SetStateAction<ColumnSort | null>>;
    }
  | { sorting?: never; onSortingChange?: never };

export type QuerySetDataTableProps<T, F = unknown> = SupportedDataTableProps<
  T,
  F
> & {
  filter?: never;
  isLoading: boolean;
  onPageIndexChange: (value: number) => void;
  onPageSizeChange: (value: PageSize) => void;
  pageIndex: number;
  pageSize: PageSize;
  rowCount?: number;
} & QuerySetSelectionProps<T> &
  QuerySetSortingProps;

export default function QuerySetDataTable<T, F = unknown>({
  columns: realColumns,
  createPlaceholder,
  enableRowSelection,
  isLoading,
  rowCount = -1,
  rows: maybeRows,
  onSortingChange,
  sorting,
  ...tableProps
}: QuerySetDataTableProps<T, F>) {
  useEffect(() => {
    if (!createPlaceholder) {
      return;
    }
    const identifier = tableProps.getIdentifier?.(createPlaceholder(1) as T);
    if (!identifier) {
      console.error(
        "Invalid placeholder passed to <QuerySetDataTable />. It must be compatible with `getIdentifier`",
      );
    }
  }, [createPlaceholder]);

  const { columns, rows } = useMemo(() => {
    if (!isLoading || !createPlaceholder) {
      return { columns: realColumns, rows: maybeRows };
    }
    const column = createColumnHelper<T>();
    const columns = realColumns.map((col) =>
      column.display({
        id: getColumnId(col),
        header: col.header as string,
        cell: () => <Skeleton />,
      }),
    );
    const rows = Array.from(Array(tableProps.pageSize)).map(
      (_value, index) => createPlaceholder(index) as T,
    );

    return { columns, rows };
  }, [
    isLoading,
    maybeRows,
    tableProps.pageSize,
    createPlaceholder,
    realColumns,
  ]);

  const selectionProps = useMemo<
    Pick<QuerySetSelectionProps<T>, "enableRowSelection">
  >(() => {
    return {
      enableRowSelection: isLoading ? () => false : enableRowSelection,
    };
  }, [enableRowSelection, isLoading]);

  const handleSortingChange = useCallback<
    Dispatch<SetStateAction<[ColumnSort] | []>>
  >(
    (sorts) => {
      onSortingChange?.((prev) => {
        if (typeof sorts === "function") {
          const next = sorts(prev ? [prev] : []);
          if (next.length === 0) {
            return null;
          }
          return next[0];
        }
        if (sorts.length === 0) {
          return null;
        }
        return sorts[0];
      });
    },
    [onSortingChange],
  );

  const dataTableProps = {
    ...tableProps,
    ...selectionProps,
    columns,
    onSortingChange: handleSortingChange,
    rowCount,
    rows,
    serverSidePagination: true,
    sorting,
  } as DataTableProps<T, F>;

  return <DataTable<T, F> {...dataTableProps} />;
}
